import { type AxePurchaseState } from '@deque/billing-service-client';
import { ProductSlugs } from '@deque/billing-utils';
import {
  Button,
  Checkbox,
  Combobox,
  ComboboxOption,
  Loader,
  RadioGroup,
  Select,
  TextField
} from '@deque/cauldron-react';
import type { RadioItem } from '@deque/cauldron-react/lib/components/RadioGroup';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ACCOUNT_PORTAL_SLUGS,
  CREATABLE_HELPDESK_SUBSCRIPTION_PRODUCTS,
  DEVTOOLS_SLUGS,
  ENTERPRISE_LICENSE_COUNT,
  HELPDESK_SUBSCRIPTION_PRODUCTS,
  INSTALL_LOCATIONS,
  NO_LICENSE_SLUGS,
  PAGE_COUNT_OPTIONS,
  PRODUCT_URL_REQUIRED_SLUGS,
  USER_LICENSE_SLUGS
} from '~/common/utils/admin-client/constants';
import { lookupZendeskTicket } from '~/common/utils/admin-client/zendesk';
import { TRIAL_STATES } from '~/modules/common/constants';
import type { SelectedEnterprise } from '../../pages/internal/admin/types';
import styles from './SubscriptionForm.css';

const productLabelForSlug = (slug: string): string =>
  HELPDESK_SUBSCRIPTION_PRODUCTS.find(product => product.value === slug)
    ?.label ?? slug;

const productSlugForLabel = (label: string): ProductSlugs | undefined =>
  HELPDESK_SUBSCRIPTION_PRODUCTS.find(product => product.label === label)
    ?.value;

export type EditAction = 'extend_trial' | null;

export interface SubscriptionFormData {
  zendeskTicket: string;
  customerName: string;
  customerDomain: string;
  customerEmail: string;
  customerFirstName: string;
  customerLastName: string;
  licenseStartDate: string;
  licenseEndDate: string;
  trialEndDate: string;
  licenseType: string;
  numberOfLicenses: string;
  pageCount: number;
  linesOfCode: string;
  unlimitedLoc: boolean;
  productUrl: string;
  monitorGroupPath: string;
  requestorEmail: string;
  salesforceAccountUrl: string;
  notifyCustomerEmail: boolean;
  autoAddUsers: boolean;
  addExistingUsers: boolean;
  makeCustomerContactAdmin: boolean;
  installLocation: string;
  monitorV8: boolean;
  existingUserAsAdmin: string;
  purchaseState: AxePurchaseState;
  editAction: EditAction;
}

export const DEFAULT_FORM_VALUES: SubscriptionFormData = {
  zendeskTicket: '',
  customerName: '',
  customerDomain: '',
  customerEmail: '',
  customerFirstName: '',
  customerLastName: '',
  licenseStartDate: '',
  licenseEndDate: '',
  trialEndDate: '',
  licenseType: 'number',
  numberOfLicenses: '',
  pageCount: 25000,
  linesOfCode: '',
  unlimitedLoc: false,
  productUrl: '',
  monitorGroupPath: '',
  requestorEmail: '',
  salesforceAccountUrl: '',
  notifyCustomerEmail: false,
  autoAddUsers: false,
  addExistingUsers: false,
  makeCustomerContactAdmin: true,
  installLocation: INSTALL_LOCATIONS.DEFAULT,
  monitorV8: false,
  existingUserAsAdmin: '',
  purchaseState: 'paid' as AxePurchaseState,
  editAction: null
};

export interface SubscriptionFormProps {
  preloadCustomerData: SelectedEnterprise;
  token?: string;
  isTrial?: boolean;
  initialProduct?: string;
  initialValues?: Partial<SubscriptionFormData>;
  /** Lock the product selector so it cannot be changed (useful for edit mode) */
  productLocked?: boolean;
  /** When true, the form is in edit mode — some fields are hidden or read-only */
  isEditMode?: boolean;
  /** Label for the submit button. Defaults to 'Submit'. */
  submitLabel?: string;
  onSubmit?: (
    data: SubscriptionFormData & { selectedProduct: string }
  ) => Promise<void>;
  onCancel?: () => void;
  onViewSubscriptions?: () => void;
  isSuperAdmin?: boolean;
}

const SubscriptionForm: React.FC<SubscriptionFormProps> = ({
  preloadCustomerData,
  token,
  isTrial = false,
  initialProduct = '',
  initialValues,
  productLocked = false,
  isEditMode = false,
  submitLabel,
  onSubmit,
  onCancel,
  isSuperAdmin = false
}) => {
  const { t } = useTranslation();
  const [selectedProduct, setSelectedProduct] =
    useState<string>(initialProduct);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');
  const [zendeskLoading, setZendeskLoading] = useState(false);
  const [zendeskError, setZendeskError] = useState<string>('');
  const [formData, setFormData] = useState<SubscriptionFormData>({
    ...DEFAULT_FORM_VALUES,
    ...initialValues
  });

  const [domainError, setDomainError] = useState<string>('');

  const isMonitor = selectedProduct === ProductSlugs.axeMonitor;
  const isLinter = selectedProduct === ProductSlugs.axeLinter;
  const isDequeu = selectedProduct === ProductSlugs.dequeUniversity;
  const isAuditorSync = selectedProduct === ProductSlugs.axeAuditorIntegration;

  const isDevTools = DEVTOOLS_SLUGS.has(selectedProduct);

  const isUserLicenseProduct = USER_LICENSE_SLUGS.has(selectedProduct);

  const supportsAccountPortal = ACCOUNT_PORTAL_SLUGS.has(selectedProduct);

  const isOnlySaas = isDequeu || isLinter;

  const supportsProductUrl = PRODUCT_URL_REQUIRED_SLUGS.has(selectedProduct);

  const isNoLicenseProduct = NO_LICENSE_SLUGS.has(selectedProduct);

  const isSaasEnvironment =
    formData.installLocation === INSTALL_LOCATIONS.DEFAULT;

  const requiresProductUrl =
    isMonitor || (supportsProductUrl && !isSaasEnvironment);

  useEffect(() => {
    if (isAuditorSync || selectedProduct === ProductSlugs.customIntegration) {
      setFormData(prev => ({
        ...prev,
        autoAddUsers: true,
        addExistingUsers: true
      }));
    }
  }, [selectedProduct, isAuditorSync]);

  useEffect(() => {
    if (isDevTools) {
      setFormData(prev => ({
        ...prev,
        notifyCustomerEmail: true
      }));
    }
  }, [isDevTools]);

  const handleFieldChange = useCallback(
    (field: keyof SubscriptionFormData) =>
      (
        e:
          | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
          | string
          | boolean
      ) => {
        const value =
          typeof e === 'string' || typeof e === 'boolean'
            ? e
            : e.target.type === 'checkbox'
              ? (e.target as HTMLInputElement).checked
              : e.target.value;

        setFormData(prev => ({ ...prev, [field]: value }));
      },
    []
  );

  const validateDomain = (domain: string): boolean => {
    if (domain.length > 0) {
      const periodIndex = domain.indexOf('.');
      if (periodIndex < 1) {
        setDomainError(t("Customer domain must be in form 'deque.com'"));
        return false;
      }
    }
    setDomainError('');
    return true;
  };

  const handleDomainBlur = () => {
    validateDomain(formData.customerDomain);
  };

  const handleInstallLocationChange = (value: string) => {
    setFormData(prev => {
      const newData = { ...prev, installLocation: value };

      if (value !== INSTALL_LOCATIONS.DEFAULT) {
        newData.autoAddUsers = true;
        newData.addExistingUsers = false;
        newData.notifyCustomerEmail = false;
        newData.makeCustomerContactAdmin = false;
        newData.customerEmail = '';
        newData.customerFirstName = '';
        newData.customerLastName = '';

        if (isMonitor) {
          newData.monitorV8 = true;
        }
      } else {
        newData.monitorGroupPath = '';
      }

      return newData;
    });
  };

  useEffect(() => {
    if (preloadCustomerData) {
      const adminMember = preloadCustomerData.enterpriseMembers.find(
        member => member.is_admin && 'email' in member
      );
      const adminEmail =
        adminMember && 'email' in adminMember ? adminMember.email : '';

      setFormData(prev => ({
        ...prev,
        customerName:
          initialValues?.customerName ??
          preloadCustomerData.enterpriseExplorer.customer_name,
        customerDomain:
          initialValues?.customerDomain ??
          preloadCustomerData.enterpriseExplorer.customer_domain,
        customerEmail: initialValues?.customerEmail ?? adminEmail
      }));
    }
  }, [preloadCustomerData]);

  const handleZendeskTicketBlur = useCallback(async () => {
    const ticketId = formData.zendeskTicket.trim();
    if (!ticketId || !token) {
      setZendeskError('');
      return;
    }
    setZendeskLoading(true);
    setZendeskError('');
    try {
      await lookupZendeskTicket({ token, ticketId });
    } catch {
      setZendeskError(t('Zendesk ticket not found or could not be loaded'));
    } finally {
      setZendeskLoading(false);
    }
  }, [formData.zendeskTicket, token, t]);

  const handleUnlimitedLocChange = (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      unlimitedLoc: checked,
      linesOfCode: checked ? '' : prev.linesOfCode
    }));
  };

  const handleLicenseTypeChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      licenseType: value,
      numberOfLicenses: value === 'enterprise' ? '' : prev.numberOfLicenses
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateDomain(formData.customerDomain)) {
      return;
    }

    setSubmitError('');
    setSubmitting(true);
    try {
      await onSubmit?.({ ...formData, selectedProduct });
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : t('An unexpected error occurred')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onChangeProduct = (product: string) => {
    setSelectedProduct(product);
  };

  return (
    <div>
      <form onSubmit={handleSubmit} autoComplete="off">
        <Combobox
          label={t('Product')}
          name="product"
          defaultValue={productLabelForSlug(initialProduct)}
          autoComplete="off"
          disabled={productLocked}
          onSelectionChange={({ value }) => {
            const slug = productSlugForLabel(String(value));
            if (slug) {
              onChangeProduct(slug);
            }
          }}
        >
          {CREATABLE_HELPDESK_SUBSCRIPTION_PRODUCTS.map(product => (
            <ComboboxOption
              key={product.value}
              value={product.label}
              formValue={product.value}
            >
              {product.label}
            </ComboboxOption>
          ))}
        </Combobox>

        {selectedProduct && (
          <>
            {productLocked && (
              <>
                <TextField
                  label={t('Purchase State')}
                  name="purchaseState"
                  value={formData.purchaseState}
                  disabled
                />

                {TRIAL_STATES.includes(formData.purchaseState) && (
                  <div>
                    <Button
                      type="button"
                      variant={
                        formData.editAction === 'extend_trial'
                          ? 'primary'
                          : 'secondary'
                      }
                      onClick={() =>
                        setFormData(prev => ({
                          ...prev,
                          editAction:
                            prev.editAction === 'extend_trial'
                              ? null
                              : 'extend_trial'
                        }))
                      }
                    >
                      {t('Extend Trial')}
                    </Button>
                  </div>
                )}
              </>
            )}

            {!isEditMode && (
              <>
                <TextField
                  label={t('Zendesk Ticket')}
                  name="zendeskTicket"
                  value={formData.zendeskTicket}
                  onChange={handleFieldChange('zendeskTicket')}
                  onBlur={handleZendeskTicketBlur}
                  error={zendeskError || undefined}
                />
                {zendeskLoading && (
                  <Loader label={t('Looking up Zendesk ticket...')} />
                )}
              </>
            )}

            <TextField
              label={t('Customer Name')}
              name="customerName"
              required
              value={formData.customerName}
              onChange={handleFieldChange('customerName')}
              disabled={isEditMode && !isSuperAdmin}
            />

            <TextField
              label={t('Customer Domain')}
              name="customerDomain"
              required
              value={formData.customerDomain}
              onChange={handleFieldChange('customerDomain')}
              onBlur={handleDomainBlur}
              error={domainError}
              disabled={isEditMode && !isSuperAdmin}
            />

            {isTrial ? (
              <TextField
                label={t('Trial End Date (YYYY-MM-DD)')}
                name="trialEndDate"
                value={formData.trialEndDate}
                onChange={handleFieldChange('trialEndDate')}
                placeholder="YYYY-MM-DD"
              />
            ) : (
              <>
                {productLocked &&
                  TRIAL_STATES.includes(formData.purchaseState) && (
                    <TextField
                      type="date"
                      label={t('Trial End Date')}
                      name="trialEndDate"
                      value={formData.trialEndDate}
                      onChange={handleFieldChange('trialEndDate')}
                      disabled={formData.editAction !== 'extend_trial'}
                    />
                  )}
                <TextField
                  type="date"
                  label={t('License Start Date')}
                  name="licenseStartDate"
                  required={!isDequeu && !productLocked}
                  value={formData.licenseStartDate}
                  onChange={handleFieldChange('licenseStartDate')}
                  disabled={isDequeu || productLocked || isEditMode}
                />
                <TextField
                  type="date"
                  label={t('License End Date')}
                  name="licenseEndDate"
                  required={!isDequeu && !productLocked}
                  value={formData.licenseEndDate}
                  onChange={handleFieldChange('licenseEndDate')}
                  disabled={isDequeu || productLocked}
                />
              </>
            )}

            {isMonitor && (
              <Checkbox
                id="monitor-v8-checkbox"
                label={t('Monitor V8 Installation?')}
                name="monitorV8"
                checked={formData.monitorV8}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFieldChange('monitorV8')(e.target.checked)
                }
                disabled={!isSaasEnvironment}
              />
            )}

            {isMonitor && (
              <fieldset disabled={productLocked}>
                <legend>{t('Page Count')}</legend>
                <RadioGroup
                  name="pageCount"
                  value={String(formData.pageCount)}
                  onChange={(radio: RadioItem) =>
                    setFormData(prev => ({
                      ...prev,
                      pageCount: parseInt(radio.value || '0', 10)
                    }))
                  }
                  radios={PAGE_COUNT_OPTIONS.map(option => ({
                    label:
                      option.value === ENTERPRISE_LICENSE_COUNT
                        ? t('Enterprise')
                        : option.label,
                    value: String(option.value),
                    id: `pageCount-${option.value}`
                  }))}
                />
              </fieldset>
            )}

            {isUserLicenseProduct && !isMonitor && (
              <>
                <fieldset disabled={productLocked}>
                  <legend>{t('Number of Licenses')}</legend>
                  <RadioGroup
                    name="licenseType"
                    value={formData.licenseType}
                    onChange={(radio: RadioItem) =>
                      handleLicenseTypeChange(radio.value || 'number')
                    }
                    radios={[
                      {
                        label: t('Enterprise'),
                        value: 'enterprise',
                        id: 'licenseType-enterprise'
                      },
                      {
                        label: t('Number of Seats'),
                        value: 'number',
                        id: 'licenseType-number'
                      }
                    ]}
                  />
                </fieldset>
                <TextField
                  label={t('Number of Seats')}
                  name="numberOfLicenses"
                  type="number"
                  required={formData.licenseType === 'number' && !productLocked}
                  value={formData.numberOfLicenses}
                  onChange={handleFieldChange('numberOfLicenses')}
                  disabled={
                    formData.licenseType === 'enterprise' ||
                    isDequeu ||
                    productLocked
                  }
                />
              </>
            )}

            {isLinter && (
              <div>
                <TextField
                  label={t('Lines of Code')}
                  name="linesOfCode"
                  required={!formData.unlimitedLoc}
                  value={formData.linesOfCode}
                  onChange={handleFieldChange('linesOfCode')}
                  disabled={formData.unlimitedLoc || productLocked}
                />
                <Checkbox
                  id="unlimited-loc-checkbox"
                  label={t('Unlimited LOC')}
                  name="unlimitedLoc"
                  checked={formData.unlimitedLoc}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleUnlimitedLocChange(e.target.checked)
                  }
                  disabled={productLocked}
                />
              </div>
            )}

            {supportsAccountPortal && (
              <>
                <Checkbox
                  id="auto-add-users-checkbox"
                  label={t('Auto Add Users?')}
                  name="autoAddUsers"
                  checked={formData.autoAddUsers}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleFieldChange('autoAddUsers')(e.target.checked)
                  }
                />
                {!isEditMode && (
                  <Checkbox
                    id="add-existing-users-checkbox"
                    label={t('Add Existing Users for auto-add?')}
                    name="addExistingUsers"
                    checked={formData.addExistingUsers}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleFieldChange('addExistingUsers')(e.target.checked)
                    }
                    disabled={!formData.autoAddUsers || !isSaasEnvironment}
                  />
                )}
              </>
            )}

            {!isOnlySaas && (
              <Select
                label={t('Install Location')}
                name="installLocation"
                value={formData.installLocation}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  handleInstallLocationChange(e.target.value)
                }
                disabled={isEditMode}
              >
                {Object.values(INSTALL_LOCATIONS).map(location => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </Select>
            )}

            <TextField
              label={t('Customer Email Address')}
              name="customerEmail"
              type="email"
              value={formData.customerEmail}
              onChange={handleFieldChange('customerEmail')}
              disabled={!isSaasEnvironment || isEditMode}
            />

            {!isEditMode && (
              <Checkbox
                id="make-customer-contact-admin-checkbox"
                label={t('Make Customer Contact an Admin?')}
                name="makeCustomerContactAdmin"
                checked={formData.makeCustomerContactAdmin}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFieldChange('makeCustomerContactAdmin')(
                    e.target.checked
                  )
                }
                disabled={!isSaasEnvironment}
              />
            )}

            <div>
              <TextField
                label={t('Customer First Name')}
                name="customerFirstName"
                value={formData.customerFirstName}
                onChange={handleFieldChange('customerFirstName')}
                disabled={!isSaasEnvironment || isEditMode}
              />
              <TextField
                label={t('Customer Last Name')}
                name="customerLastName"
                value={formData.customerLastName}
                onChange={handleFieldChange('customerLastName')}
                disabled={!isSaasEnvironment || isEditMode}
              />
            </div>

            {isDevTools && !isEditMode && (
              <Checkbox
                id="notify-customer-email-checkbox"
                label={t('Notify Customer Email')}
                name="notifyCustomerEmail"
                checked={formData.notifyCustomerEmail}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFieldChange('notifyCustomerEmail')(e.target.checked)
                }
              />
            )}

            {isSuperAdmin && !isNoLicenseProduct && (
              <TextField
                label={t('Existing User as Admin')}
                name="existingUserAsAdmin"
                value={formData.existingUserAsAdmin}
                onChange={handleFieldChange('existingUserAsAdmin')}
              />
            )}

            <TextField
              label={t('Product URL') + (requiresProductUrl ? '*' : '')}
              name="productUrl"
              required={requiresProductUrl}
              value={formData.productUrl}
              onChange={handleFieldChange('productUrl')}
              disabled={isSaasEnvironment && !isMonitor && !isEditMode}
            />

            {isMonitor && !isSaasEnvironment && (
              <TextField
                label={t('Monitor Group Path') + '*'}
                name="monitorGroupPath"
                required
                value={formData.monitorGroupPath}
                onChange={handleFieldChange('monitorGroupPath')}
              />
            )}

            {!isEditMode && (
              <TextField
                label={t('Requestor Email Address')}
                name="requestorEmail"
                value={formData.requestorEmail}
                onChange={handleFieldChange('requestorEmail')}
              />
            )}

            <TextField
              label={t('Salesforce Account URL')}
              name="salesforceAccountUrl"
              value={formData.salesforceAccountUrl}
              onChange={handleFieldChange('salesforceAccountUrl')}
            />

            <div className={styles.formActions}>
              <Button
                type="submit"
                variant="primary"
                disabled={
                  submitting ||
                  (productLocked &&
                    TRIAL_STATES.includes(formData.purchaseState) &&
                    formData.editAction === null)
                }
              >
                {submitting ? t('Submitting...') : (submitLabel ?? t('Submit'))}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={onCancel}
                disabled={submitting}
              >
                {t('Cancel')}
              </Button>
            </div>
            {submitError && <p role="alert">{submitError}</p>}
          </>
        )}
      </form>
    </div>
  );
};

export default SubscriptionForm;
